import { Button, Divider, Drawer, Stack, Typography } from '@mui/material'
import {
  type ResponseFormula,
  type ResponseFormulaDecimalPoint,
  ResponseFormulaSubTypeEnum,
  type RuleDefTypeEnum,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { type FormikHelpers, useFormik } from 'formik'
import type { FC } from 'react'
import {
  isNumberRuleOutOfRange,
  numberRuleOutOfRangeMessage,
} from 'src/constants/responseNumber'
import FractionalDigitsRow, {
  MAX_FORMULA_FRACTIONAL_DIGITS,
} from './FractionalDigitsRow'
import NormalValueRow from './NormalValueRow'

interface OwnProps {
  isOpen: boolean
  questionNode: TemplateNodeSchema
  response?: ResponseFormula
  onClose: () => void
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

export type FormulaRuleProps = {
  hasRule: boolean
  minimum?: number
  maximum?: number
  scale?: string
  subType?: ResponseFormulaSubTypeEnum
  decimalPoint?: ResponseFormulaDecimalPoint
  ruleDefType?: RuleDefTypeEnum
}

const EditResponseFormulaRuleDrawerContainer: FC<OwnProps> = ({
  response,
  questionNode,
  onClose,
  isOpen,
  onSave,
}) => {
  // 計算式タイプの質問にルールを設定する
  const changeResponseFormulaRule = (
    scale: string,
    originalResponse: ResponseFormula,
    newResponseParams: Pick<
      ResponseFormula,
      'minimum' | 'maximum' | 'subType' | 'decimalPoint'
    >,
  ) => {
    const newResponse: ResponseFormula = {
      ...originalResponse,
      ...newResponseParams,
    }
    onSave({ scale, responseFormulas: [newResponse] })
  }

  // subType が 0 である場合にもルールなしの判定としたいが、
  // 0 は Enum に定義されておらず型エラーが発生するため、falsy かどうかで判定する
  const hasRule = !!response?.subType

  const initialValues: FormulaRuleProps = {
    hasRule,
    minimum: response?.minimum,
    maximum: response?.maximum,
    scale: questionNode.question?.scale,
    subType: response?.subType || undefined,
    decimalPoint: {
      value: response?.decimalPoint?.value,
      isActive: response?.decimalPoint?.isActive,
    },
  }

  const formikProps = useFormik({
    initialValues: initialValues,
    validate: ({
      hasRule,
      subType,
      minimum,
      maximum,
      decimalPoint,
    }: FormulaRuleProps) => {
      const errors: Partial<Record<keyof FormulaRuleProps, string>> = {}

      if (hasRule) {
        if (!subType) {
          errors.subType = 'ルールの設定を行ってください'
          return errors
        }

        switch (subType) {
          case ResponseFormulaSubTypeEnum.BETWEEN:
            if (minimum === undefined || minimum === null) {
              errors.minimum = '最小値を入力してください'
              return errors
            }
            if (Number.isNaN(minimum)) {
              errors.minimum = '最小値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(minimum)) {
              errors.minimum = numberRuleOutOfRangeMessage('最小値')
              return errors
            }
            if (maximum === undefined || maximum === null) {
              errors.maximum = '最大値を入力してください'
              return errors
            }
            if (Number.isNaN(maximum)) {
              errors.maximum = '最大値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(maximum)) {
              errors.maximum = numberRuleOutOfRangeMessage('最大値')
              return errors
            }
            if (minimum > maximum) {
              errors.minimum = '最小値 ー 最大値 で入力してください'
              return errors
            }
            break
          case ResponseFormulaSubTypeEnum.LESS_THAN:
            if (maximum === undefined || maximum === null) {
              errors.maximum = '最大値を入力してください'
              return errors
            }
            if (Number.isNaN(maximum)) {
              errors.maximum = '最大値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(maximum)) {
              errors.maximum = numberRuleOutOfRangeMessage('最大値')
              return errors
            }
            break
          case ResponseFormulaSubTypeEnum.GREATER_THAN:
            if (minimum === undefined || minimum === null) {
              errors.minimum = '最小値を入力してください'
              return errors
            }
            if (Number.isNaN(minimum)) {
              errors.minimum = '最小値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(minimum)) {
              errors.minimum = numberRuleOutOfRangeMessage('最小値')
              return errors
            }
            break
        }
      }

      if (decimalPoint) {
        const { isActive, value } = decimalPoint
        if (isActive && value === undefined) {
          errors.decimalPoint = '小数点以下の桁数を入力してください。'
          return errors
        }
        if (
          isActive &&
          value !== undefined &&
          value > MAX_FORMULA_FRACTIONAL_DIGITS
        ) {
          errors.decimalPoint = `小数点以下の桁数は${MAX_FORMULA_FRACTIONAL_DIGITS}以下で入力してください。`
          return errors
        }
        if (!!subType && isActive && value !== undefined) {
          const maximumDecimalPoint = String(maximum).split('.')[1]?.length || 0
          const minimumDecimalPoint = String(minimum).split('.')[1]?.length || 0
          const ruleDecimalPoint =
            maximumDecimalPoint > minimumDecimalPoint
              ? maximumDecimalPoint
              : minimumDecimalPoint
          if (value < ruleDecimalPoint) {
            errors.decimalPoint =
              '小数点以下の桁数をルールの小数点桁数より大きい数値で入力してください。'
            return errors
          }
        }
      }

      return errors
    },
    onSubmit: (
      { subType, minimum, maximum, decimalPoint, scale }: FormulaRuleProps,
      { resetForm }: FormikHelpers<FormulaRuleProps>,
    ) => {
      if (subType === ResponseFormulaSubTypeEnum.GREATER_THAN) {
        maximum = undefined
      }
      if (subType === ResponseFormulaSubTypeEnum.LESS_THAN) {
        minimum = undefined
      }

      changeResponseFormulaRule(scale ?? '', response || {}, {
        minimum,
        maximum,
        subType,
        decimalPoint,
      })
      onClose()
      resetForm()
    },
    enableReinitialize: true,
  })

  const { handleSubmit, resetForm } = formikProps

  const onCloseOrCancel = () => {
    onClose()
    resetForm()
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onCloseOrCancel()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 600,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          ルールの設定
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <NormalValueRow {...{ formikProps }} />
          <FractionalDigitsRow {...{ formikProps }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={onCloseOrCancel}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default EditResponseFormulaRuleDrawerContainer
